import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useState, useMemo, useRef, useEffect } from "react";
import dayjs from "dayjs";
import { Collapse } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { useRecordEvent } from "app/hook";
import { AnalyticsEventType } from "app/types";
import { AppDispatch, RootState } from "app/store";
import Constants from "common/constant";
import { Spin, UserAvatar } from "common/elements";
import i18n from "features/shared/i18n";
import { usePlanPrice } from "features/subscription/usePlanPrice";
import { usePurchaseFlow } from "features/subscription/usePurchaseFlow";
import {
  MessageDate,
  ChatMessageItem,
  OfficialChatMessageItem,
} from "features/message/components";
import {
  sendChatRoomMessage,
  getParentChatRoomMessages,
  sendContactChatRoomMessage,
} from "features/message/api";
import {
  ChatRoomMemberType,
  ChatRoomMessage,
  ChatRoomType,
  FormMessageContactReply,
  MessageContentType,
  MessageType,
} from "features/message/types";
import {
  messageActions,
  selectAllParentChatRooms,
  selectAllParentChatRoomMessages,
  selectParentChatRoomMessageSyncById,
  selectAllParentChatRoomMessageSync,
} from "features/message/slice";
import { ParentChatInputBar } from "./components";

const OFFICIAL_ICON = `${Constants.assetHost}/assets/images/app_official_icon.png`;

// サポートルームの定型メッセージを表示用テキストに変換する
const formContactMessageContent = (message: ChatRoomMessage) => {
  switch (message.message_type) {
    case MessageType.CONTACT_INITIAL:
      return { text: i18n.t("MSG_PARENT_PAGE_SUPPORT_WELCOME") };
    case MessageType.CONTACT_REPLY: {
      const reply = message.message_content as FormMessageContactReply;
      return { title: reply.title, text: reply.content };
    }
    case MessageType.CONTACT_INPUT_AUTO_REPLY:
      return {
        text: i18n.t("MSG_USER_PROFILE_MESSAGE_CONTACT_INPUT_AUTO_REPLY"),
      };
    default:
      return message.message_content;
  }
};

export const MessagePage = () => {
  const navigate = useNavigate();
  const handleRecordEvent = useRecordEvent();
  const dispatch = useDispatch<AppDispatch>();
  const { purchase } = usePurchaseFlow();
  const { getPriceDisplay } = usePlanPrice();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [refresh, setRefresh] = useState(false);
  const chatRooms = useSelector(selectAllParentChatRooms);
  const allMessages = useSelector(selectAllParentChatRoomMessages);
  const users = useSelector((state: RootState) => state.user.users);
  const parent = useSelector((state: RootState) => state.user.parent);
  const isRefreshed = useSelector(
    (state: RootState) => state.message.isRefreshed,
  );
  const allParentChatRoomMessageSync = useSelector(
    selectAllParentChatRoomMessageSync,
  );
  const selectedChatRoomId = useSelector(
    (state: RootState) => state.message.selectedChatRoomId,
  );
  const chatRoomMessageSync = useSelector((state: RootState) =>
    selectedChatRoomId
      ? selectParentChatRoomMessageSyncById(state, selectedChatRoomId)
      : null,
  );

  const parentChatRoomLookUp = Array.from(allParentChatRoomMessageSync).reduce(
    (acc, sync) =>
      Object.assign(acc, { [sync.id]: sync.latestMessage?.created_at }),
    {},
  );

  const contactRoom = chatRooms.find(
    (room) => room.type === ChatRoomType.CONTACT,
  );

  const sortedParentChatRoom = chatRooms
    .filter(
      (room) =>
        room.type === ChatRoomType.TALK && parentChatRoomLookUp[room.id],
    )
    .sort((a, b) =>
      dayjs(parentChatRoomLookUp[a.id], "YYYY-MM-DDTHH:mm:ssZ").diff(
        dayjs(parentChatRoomLookUp[b.id], "YYYY-MM-DDTHH:mm:ssZ"),
      ) > 0
        ? -1
        : 1,
    );

  // サポートルームをデフォルト選択（データ到着後、未選択の場合のみ）
  const contactRoomId = contactRoom?.id;
  useEffect(() => {
    if (!selectedChatRoomId && contactRoomId) {
      dispatch(messageActions.setSelectedChatRoomId(contactRoomId));
    }
  }, [selectedChatRoomId, contactRoomId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(messageActions.setSelectedChatRoomId(null));
    };
  }, [dispatch]);

  const selectedRoom = useMemo(() => {
    return chatRooms.find((room) => room.id === selectedChatRoomId);
  }, [chatRooms, selectedChatRoomId]);

  const messages = useMemo(() => {
    return allMessages
      .filter((msg) => msg.chat_room_id === selectedChatRoomId)
      .sort((a, b) =>
        dayjs(a.created_at).isBefore(dayjs(b.created_at)) ? -1 : 1,
      );
  }, [allMessages, selectedChatRoomId]);

  const getChildUser = (room: typeof selectedRoom) => {
    if (!room) return null;
    const kidsMember = room.members?.find(
      (m) => m.type === ChatRoomMemberType.KIDS,
    );
    if (!kidsMember) return null;
    return users.find((u) => u.uid === kidsMember.uid);
  };

  const isRoomNew = (roomId: string) => {
    const chatRoomSync = allParentChatRoomMessageSync.find(
      (sync) => sync.id === roomId,
    );
    return (
      selectedChatRoomId !== roomId &&
      chatRoomSync &&
      (dayjs(chatRoomSync.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
        dayjs(chatRoomSync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ"),
      ) > 0 ||
        (chatRoomSync.latestReadAt === null &&
          chatRoomSync.latestMessageCreatedAt !== null))
    );
  };

  useEffect(() => {
    if (scrollRef.current) {
      if (isRefreshed) {
        scrollRef.current.scrollTop = 200;
        dispatch(messageActions.setIsRefreshed(false));
      } else {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChatRoomId, messages.length]);

  const handleChatRoomClick = (roomId: string) => {
    dispatch(messageActions.setSelectedChatRoomId(roomId));
  };

  const handleSendMessage = async (text: string, imageUrl?: string) => {
    if (!selectedChatRoomId || (!text.trim() && !imageUrl) || !parent) return;

    const hasImage = !!imageUrl;
    const hasText = !!text.trim();
    const contentType = hasImage
      ? hasText
        ? MessageContentType.IMAGE_WITH_TEXT
        : MessageContentType.IMAGE
      : MessageContentType.TEXT;
    const content = hasImage
      ? hasText
        ? { url: imageUrl, text: text.trim() }
        : { url: imageUrl }
      : { text };

    const message = {
      user_uid: parent.uid,
      user_type: ChatRoomMemberType.PARENTS,
      message_type: MessageType.NORMAL,
      message_content_type: contentType,
      message_content: content,
      created_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      updated_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    };

    try {
      if (selectedRoom?.type === ChatRoomType.CONTACT) {
        await sendContactChatRoomMessage({
          uid: parent.uid,
          roomId: selectedChatRoomId,
          message: {
            url: `${window.location.pathname}${window.location.search}`,
            message_content_type: contentType,
            message_content: content,
          },
        });
        handleRecordEvent(AnalyticsEventType.PARENT_MESSAGE_SEND, { room_type: "contact" });
      } else {
        await sendChatRoomMessage({ roomId: selectedChatRoomId, message });
        handleRecordEvent(AnalyticsEventType.PARENT_MESSAGE_SEND, { room_type: "talk" });
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      toast(
        <div>
          <p className="text text-textcolor-black">
            {i18n.t("MSG_TOAST_SEND_FAILED")}
          </p>
        </div>,
      );
    }
  };

  const handleRefresh = () =>
    new Promise<void>((resolve) => {
      if (!selectedChatRoomId || !chatRoomMessageSync) {
        resolve();
        return;
      }
      dispatch(messageActions.setIsRefreshed(true));
      dispatch(
        getParentChatRoomMessages({
          roomId: selectedChatRoomId,
          lastMessageSyncAt: chatRoomMessageSync.lastSyncAt,
          messageCount: 5,
          chatRoomType: selectedRoom?.type ?? ChatRoomType.TALK,
        }),
      )
        .then(unwrapResult)
        .then(() => resolve())
        .catch(() => resolve());
    });

  const handleClickRefresh = () => {
    if (!selectedChatRoomId || !chatRoomMessageSync) return;
    setRefresh(true);
    setTimeout(() => {
      dispatch(messageActions.setIsRefreshed(true));
      dispatch(
        getParentChatRoomMessages({
          roomId: selectedChatRoomId,
          lastMessageSyncAt: chatRoomMessageSync.lastSyncAt,
          messageCount: 5,
          chatRoomType: selectedRoom?.type ?? ChatRoomType.TALK,
        }),
      )
        .then(unwrapResult)
        .then(() => setRefresh(false))
        .catch(() => setRefresh(false));
    }, 500);
  };

  if (chatRooms.length === 0) {
    return (
      <div className="flex-col-el h-full items-center justify-center">
        <div className="text-[24px] text-gray2-100">
          {i18n.t("MSG_PARENT_PAGE_MESSAGE_NO_ROOMS")}
        </div>
      </div>
    );
  }

  const childUser = getChildUser(selectedRoom);
  const isContactSelected = selectedRoom?.type === ChatRoomType.CONTACT;

  return (
    <div className="flex-col-el gap-4 h-full">
      {/* チャット相手セレクタ（横一列） */}
      <div className="flex-row-el gap-3 overflow-x-auto invisible-scrollbar shrink-0">
        {contactRoom && (
          <button
            className={`relative flex-col-el items-center gap-2 px-4 py-3 min-w-[100px] rounded-xl cursor-pointer transition-all ${
              selectedChatRoomId === contactRoom.id
                ? "bg-blue-10 border-2 border-blue-100"
                : "bg-white border-2 border-transparent hover:bg-surface"
            }`}
            onClick={() => handleChatRoomClick(contactRoom.id)}
          >
            <UserAvatar size={48} icon={OFFICIAL_ICON} />
            <span className="text-[21px] text-textcolor-black">
              {i18n.t("MSG_INEXUS_OFFICIAL_CONTACT")}
            </span>
            {isRoomNew(contactRoom.id) && (
              <div className="flex items-center justify-center bg-red-80 w-[50px] h-[24px] text-white rounded-[4px] absolute top-1 right-1">
                <p className="text text-[24px]! leading-[24px]!">New</p>
              </div>
            )}
          </button>
        )}
        {sortedParentChatRoom.map((room) => {
          const roomChildUser = getChildUser(room);
          const isNew = isRoomNew(room.id);

          return (
            <button
              key={room.id}
              className={`relative flex-col-el items-center gap-2 px-4 py-3 min-w-[100px] rounded-xl cursor-pointer transition-all ${
                selectedChatRoomId === room.id
                  ? "bg-blue-10 border-2 border-blue-100"
                  : "bg-white border-2 border-transparent hover:bg-surface"
              }`}
              onClick={() => handleChatRoomClick(room.id)}
            >
              <UserAvatar size={48} icon={roomChildUser?.icon} />
              <span className="text-[21px] text-textcolor-black">
                {roomChildUser?.display_name || room.name}
              </span>
              {isNew && (
                <div className="flex items-center justify-center bg-red-80 w-[50px] h-[24px] text-white rounded-[4px] absolute top-1 right-1">
                  <p className="text text-[24px]! leading-[24px]!">New</p>
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-[16px] flex flex-col flex-1 min-h-0 overflow-hidden">
        {selectedRoom && (
          <>
            <div className="flex-row-el items-center gap-3 py-5 px-6 border-b border-outline">
              <UserAvatar
                size={40}
                icon={isContactSelected ? OFFICIAL_ICON : childUser?.icon}
              />
              <div className="text-[27px] text-textcolor-black">
                {isContactSelected
                  ? i18n.t("MSG_INEXUS_OFFICIAL_CONTACT")
                  : childUser?.display_name || selectedRoom.name}
              </div>
            </div>

            <Collapse in={refresh} sx={{ width: "100%" }}>
              <div
                className="flex-col-center w-full h-[80px]"
                style={{ display: !refresh ? "none" : undefined }}
              >
                <Spin />
              </div>
            </Collapse>

            <PullToRefresh
              className="flex-col-el flex-1 min-h-0 w-full bg-white"
              onRefresh={handleRefresh}
              pullingContent=""
              refreshingContent={<Spin />}
            >
              <div
                ref={scrollRef}
                className="h-full pt-2 flex-col-el overflow-y-auto"
              >
                <button
                  className="bg-transparent border-none my-2 cursor-pointer"
                  onClick={handleClickRefresh}
                >
                  <p className="text text-blue-100">
                    {i18n.t("MSG_PARENT_PAGE_MESSAGE_LOAD_MORE")}
                  </p>
                </button>

                {messages.map((message, index) => {
                  const isFromUser =
                    message.user_type === ChatRoomMemberType.PARENTS;
                  const isOfficial =
                    message.user_type === ChatRoomMemberType.OFFICIAL;
                  const showDate =
                    index === 0 ||
                    dayjs(
                      dayjs(message.created_at, "YYYY-MM-DDTHH:mm:ssZ").format(
                        "YYYY-MM-DD",
                      ),
                    ).diff(
                      dayjs(
                        dayjs(
                          messages[index - 1].created_at,
                          "YYYY-MM-DDTHH:mm:ssZ",
                        ).format("YYYY-MM-DD"),
                      ),
                      "days",
                    ) > 0;

                  return (
                    <div key={message.id} className="flex-col-el relative">
                      {showDate && (
                        <MessageDate timestamp={message.created_at} />
                      )}
                      {isContactSelected ? (
                        <ChatMessageItem
                          isFromUser={!isOfficial}
                          name={
                            isOfficial
                              ? i18n.t("MSG_INEXUS_OFFICIAL_CONTACT")
                              : parent?.display_name
                          }
                          icon={isOfficial ? OFFICIAL_ICON : parent?.icon}
                          messageContentType={message.message_content_type}
                          messageContent={formContactMessageContent(message)}
                          created_at={message.created_at}
                        />
                      ) : isOfficial ? (
                        <OfficialChatMessageItem message={message} />
                      ) : (
                        <ChatMessageItem
                          isFromUser={isFromUser}
                          name={
                            isFromUser
                              ? parent?.display_name
                              : childUser?.display_name
                          }
                          icon={isFromUser ? parent?.icon : childUser?.icon}
                          messageContentType={message.message_content_type}
                          messageContent={message.message_content}
                          created_at={message.created_at}
                          purchase={{
                            onPurchase: purchase,
                            onOpenPurchasePage: () => navigate("/subscription"),
                            getPriceDisplay,
                          }}
                        />
                      )}
                    </div>
                  );
                })}

                {messages.length === 0 && (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-[21px] text-gray2-100">
                      {i18n.t("MSG_PARENT_PAGE_MESSAGE_NO_MESSAGES")}
                    </div>
                  </div>
                )}
              </div>
            </PullToRefresh>

            <ParentChatInputBar
              onSend={handleSendMessage}
              disabled={!selectedChatRoomId}
              placeholder={i18n.t("MSG_PARENT_PAGE_MESSAGE_INPUT_PLACEHOLDER")}
            />
          </>
        )}
      </div>
    </div>
  );
};
